/** 应用配置 / 任务 API 封装。 */
import {
  agentEnvelopeSchema,
  appConfigResponseSchema,
  revisionListResponseSchema,
  rollbackResponseSchema,
  taskResponseSchema,
  type AgentEnvelope,
  type AppConfig,
  type Revision,
  type RollbackResponse,
  type TaskResponse,
} from '@khc/contracts';
import { ApiError, apiRequest } from './client';

export async function fetchAppConfig(hubId: string, app: string): Promise<AppConfig | null> {
  try {
    const body = await apiRequest(
      `/api/v1/nodes/${encodeURIComponent(hubId)}/apps/${encodeURIComponent(app)}`,
    );
    return appConfigResponseSchema.parse(body).app;
  } catch (error) {
    if (error instanceof ApiError && error.status === 404) return null;
    throw error;
  }
}

export async function saveAppConfig(
  hubId: string,
  app: string,
  config: Record<string, unknown>,
  note?: string,
): Promise<AppConfig> {
  const body = await apiRequest(
    `/api/v1/nodes/${encodeURIComponent(hubId)}/apps/${encodeURIComponent(app)}`,
    {
      method: 'PUT',
      body: JSON.stringify({ config, note }),
    },
  );
  return appConfigResponseSchema.parse(body).app;
}

export async function runAppAction(
  hubId: string,
  app: string,
  action: string,
  params: Record<string, unknown> = {},
  options: { idempotencyKey?: string } = {},
): Promise<AgentEnvelope> {
  const body: Record<string, unknown> = { params };
  if (options.idempotencyKey) body.idempotencyKey = options.idempotencyKey;
  const response = await apiRequest(
    `/api/v1/nodes/${encodeURIComponent(hubId)}/apps/${encodeURIComponent(app)}/${action}`,
    { method: 'POST', body: JSON.stringify(body) },
  );
  return agentEnvelopeSchema.parse(response);
}

export async function fetchTask(hubId: string, taskId: string): Promise<TaskResponse> {
  const body = await apiRequest(
    `/api/v1/nodes/${encodeURIComponent(hubId)}/tasks/${encodeURIComponent(taskId)}`,
  );
  return taskResponseSchema.parse(body);
}

/** apply 响应里 hostFw 报告的人话消息（“已自动放行 …”回显，passthrough 字段）。 */
export function hostFwMessages(envelope: AgentEnvelope): string[] {
  const record = envelope as unknown as Record<string, unknown>;
  const hostFw = record?.hostFw as { messages?: unknown } | undefined;
  if (!Array.isArray(hostFw?.messages)) return [];
  return hostFw.messages.filter((message): message is string => typeof message === 'string');
}

export async function fetchRevisions(hubId: string, app: string): Promise<Revision[]> {
  const body = await apiRequest(
    `/api/v1/nodes/${encodeURIComponent(hubId)}/apps/${encodeURIComponent(app)}/revisions`,
  );
  return revisionListResponseSchema.parse(body).revisions;
}

export async function rollbackApp(
  hubId: string,
  app: string,
  rev: number,
): Promise<RollbackResponse> {
  const body = await apiRequest(
    `/api/v1/nodes/${encodeURIComponent(hubId)}/apps/${encodeURIComponent(app)}/rollback/${rev}`,
    { method: 'POST', body: JSON.stringify({}) },
  );
  return rollbackResponseSchema.parse(body);
}
